import axios from "axios";
import moment from "moment";
import { baseUrl } from "../utils/envAccess";

/**
 * An unified clinician directory by joining two existing endpoints.
 *
 * 
 *  - Chat/GetCareGiverList returns the clinician's Rolename, which is needed to bucket
 *    clinicians into the booking categories.
 *  - Patient/GetAllDoctorsAllTimeSlots returns the numeric UserId (required by
 *    bookdoctorappointment), the profile picture and live availability.
 *
 * Both select from the same User table.
 *
 * Side Note: GetCareGivers is purposefuly not used. It needs 
 * InitialBooking AND FollowUpBooking to be not NULL, which hides most clinicians.
 */

const DEFAULT_PAGE_SIZE = 500;

function normalizeEmail(value) {
  return String(value || "").trim().toLowerCase();
}

export function normalizeRole(value) {
  return String(value || "").trim().toLowerCase();
}


const LOCATION_ADDRESSES = {
  edmonton: "11444 17 Ave SW, Edmonton, AB T6W 2S5",
  calgary: "7015 Macleod Trl SW S #400, Calgary, AB T2H 2K6",
};

export function addressForLocation(location) {
  const key = String(location || "").trim().toLowerCase();
  // Virtual availability has no address to show; the booking screen should
  // show the word "Virtual" itself rather than an address lookup.
  if (key === "virtual") return "Virtual";
  return LOCATION_ADDRESSES[key] || location;
}

function pick(obj, ...keys) {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return undefined;
}

function parseSlots(rawSlots) {
  const list = Array.isArray(rawSlots) ? rawSlots : [];
  return list
    .map((s) => {
      const start = pick(s, "start", "Start", "from", "From");
      const end = pick(s, "end", "End", "to", "To");
      
      const slotId = pick(
        s,
        "userZoomAvailabilitySlotId",
        "UserZoomAvailabilitySlotId",
        "userZoomAvailabilityId",
        "UserZoomAvailabilityId"
      );
      if (!start || !end || slotId == null) return null;

      const startM = moment.parseZone(start);
      const endM = moment.parseZone(end);
      if (!startM.isValid() || !endM.isValid()) return null;

      return {
        slotId: Number(slotId),
        start,
        end,
        startMoment: startM,
        endMoment: endM,
        date: startM.format("YYYY-MM-DD"),
        startLabel: startM.format("h:mm A"),
        endLabel: endM.format("h:mm A"),
        durationMinutes: endM.diff(startM, "minutes"),
        location: pick(s, "location", "Location") || "",
      };
    })
    .filter(Boolean)
    .filter((s) => s.startMoment.isAfter(moment()));
}

async function fetchRolesByEmail(config) {
  try {
    const res = await axios.get(
      `${baseUrl}Chat/GetCareGiverList/0/${DEFAULT_PAGE_SIZE}`,
      config
    );
    const list = Array.isArray(res?.data) ? res.data : [];
    const map = new Map();
    list.forEach((c) => {
      const email = normalizeEmail(pick(c, "email", "Email"));
      if (!email) return;
      map.set(email, {
        userRef: pick(c, "userRef", "UserRef"),
        roleName: pick(c, "rolename", "roleName", "Rolename", "RoleName") || "",
        status: pick(c, "status", "Status") || "",
        firstName: pick(c, "firstname", "firstName", "Firstname") || "",
        lastName: pick(c, "lastname", "lastName", "Lastname") || "",
      });
    });
    return map;
  } catch (e) {
    return new Map();
  }
}

async function fetchSlotsAndPhotos(config, location) {
  try {
    const res = await axios.get(`${baseUrl}Patient/GetAllDoctorsAllTimeSlots`, {
      ...config,
      params: location ? { location } : undefined,
    });
    const raw = res?.data?.data ?? res?.data;
    const list = Array.isArray(raw) ? raw : raw?.items || raw?.doctors || raw?.results || [];
    return Array.isArray(list) ? list : [];
  } catch (e) {
    return [];
  }
}

/**
 * @returns {Promise<Array<{
 *   userId: number, userRef?: string, name: string, email: string,
 *   roleName: string, picture: string|null, slots: Array, hasAvailability: boolean,
 *   initialBookingLink: string, followUpBookingLink: string, hasSufficientPoints: boolean
 * }>>}
 */
export async function fetchClinicianDirectory(token, { location = "" } = {}) {
  const config = {
    headers: {
      accept: "text/plain",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  };

  const [roleMap, doctors] = await Promise.all([
    fetchRolesByEmail(config),
    fetchSlotsAndPhotos(config, location),
  ]);

  return doctors
    .map((d) => {
      const userId = pick(d, "userId", "UserId", "doctorId", "DoctorId", "id", "Id");
      const email = normalizeEmail(pick(d, "email", "Email"));
      const name = pick(d, "doctorName", "DoctorName", "name", "Name") || "";
      if (userId == null || !name) return null;

      const roleInfo = roleMap.get(email);
      const slots = parseSlots(pick(d, "slots", "Slots"));

      return {
        userId: Number(userId),
        userRef: roleInfo?.userRef,
        name: String(name).trim(),
        email,
        // Empty when the clinician is missing from the role endpoint, 
        // treated as "uncategorized" rather than dropping them.
        roleName: roleInfo?.roleName || "",
        status: roleInfo?.status || "",
        picture:
          pick(d, "picture", "Picture", "photo", "Photo", "profile", "Profile", "avatar", "Avatar") ||
          null,
        

        bio: pick(d, "description", "Description", "bio", "Bio") || "",
        slots,
        hasAvailability: slots.length > 0,

        initialBookingLink: pick(d, "initialBookingLink", "InitialBookingLink") || "",
        followUpBookingLink: pick(d, "followUpBookingLink", "FollowUpBookingLink") || "",

        hasSufficientPoints: pick(d, "hasSufficientPoints", "HasSufficientPoints") !== false,
      };
    })
    .filter(Boolean);
}


export function cliniciansForGroup(clinicians, group) {
  const roleNames = Array.isArray(group?.careGivers) ? group.careGivers : [];
  if (!roleNames.length) return [];
  const wanted = new Set(roleNames.map(normalizeRole));
  return (clinicians || []).filter((c) => wanted.has(normalizeRole(c.roleName)));
}


export function isFollowUpForGroup(pastAppointments, group, clinicians) {
  const roleNames = Array.isArray(group?.careGivers) ? group.careGivers : [];
  if (!roleNames.length) return false;
  const wanted = new Set(roleNames.map(normalizeRole));


  const roleByName = new Map(
    (clinicians || []).map((c) => [String(c.name).trim().toLowerCase(), normalizeRole(c.roleName)])
  );

  return (pastAppointments || []).some((appt) => {
    const providerName = String(appt?.providerName || "").trim().toLowerCase();
    if (!providerName) return false;
    const role = roleByName.get(providerName);
    return role ? wanted.has(role) : false;
  });
}
